import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Html5Qrcode, Html5QrcodeCameraScanConfig } from "html5-qrcode";
import { MessageService } from "primeng/api";

@Component({
  selector: "app-qr-scanner",
  templateUrl: "./qr-scanner.component.html",
  styleUrls: ["./qr-scanner.component.css"],
})
export class QrScannerComponent implements OnInit, OnDestroy {
  isScanning = false;

  @Output() scanSuccess = new EventEmitter<string>();
  @Output() onClose = new EventEmitter<void>();

  private html5QrCode: Html5Qrcode | null = null;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
  ) {}

  /* -------------------------------------------------------------------------- */
  /* Lifecycle                                                                  */
  /* -------------------------------------------------------------------------- */

  ngOnInit(): void {
    this.startScanner();
  }

  ngOnDestroy(): void {
    this.stopScanner();
  }

  /* -------------------------------------------------------------------------- */
  /* Scanner Logic                                                              */
  /* -------------------------------------------------------------------------- */

  private async startScanner(): Promise<void> {
    this.isScanning = true;

    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      await this.initScanner();
    } catch {
      this.showCameraError("CAMERA_PERMISSION_DENIED");
      this.isScanning = false;
    }
  }

  private async initScanner(): Promise<void> {
    this.html5QrCode = new Html5Qrcode("qr-reader", {
      verbose: false,
      useBarCodeDetectorIfSupported: true, // ONLY way to get Code-128 in v2.3.8
    });

    try {
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) {
        this.showCameraError("CAMERA_NOT_FOUND");
        return;
      }

      const backCamera =
        cameras.find((c) => c.label.toLowerCase().includes("back")) ||
        cameras[cameras.length - 1];

      const config: Html5QrcodeCameraScanConfig = {
        fps: 12, // LOWER FPS = better for 1D barcodes
        qrbox: this.getBarcodeScanBox(),
        aspectRatio: 16 / 9,
        disableFlip: true,
        videoConstraints: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      await this.html5QrCode.start(
        backCamera.id,
        config,
        (decodedText: string) => {
          this.scanSuccess.emit(decodedText);
          this.stopScanner();
        },
        () => {
          // Ignore decode errors per frame
        },
      );
    } catch {
      this.showCameraError("CAMERA_ACCESS_ERROR");
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Helpers                                                                    */
  /* -------------------------------------------------------------------------- */

  private getBarcodeScanBox(): { width: number; height: number } {
    // Wide rectangular box REQUIRED for Code-128
    const width = Math.min(window.innerWidth * 0.9, 600);
    const height = Math.min(window.innerHeight * 0.4, 400);
    return { width, height };
  }

  private showCameraError(key: string): void {
    this.messageService.add({
      severity: "error",
      summary: this.translate.instant("DOOR_SCAN_SOLUTION.SCAN_ERROR_SUMMARY"),
      detail: this.translate.instant(`DOOR_SCAN_SOLUTION.${key}`),
    });
  }

  /* -------------------------------------------------------------------------- */
  /* Cleanup                                                                    */
  /* -------------------------------------------------------------------------- */

  stopScanner(): void {
    if (!this.html5QrCode) {
      this.isScanning = false;
      this.onClose.emit();
      return;
    }

    this.html5QrCode
      .stop()
      .then(() => this.html5QrCode?.clear())
      .catch(() => {
        this.showCameraError("STOP_ERROR_DETAIL");
      })
      .finally(() => {
        this.html5QrCode = null;
        this.isScanning = false;
        this.onClose.emit();
      });
  }
}
